import { Typography } from '@mui/material'
import React from 'react'
import * as datefns from 'date-fns'
import { useAppLocale } from '../../i18n/localeUtils'
import { EnrichedWarrantEntity, WarrantEntity } from 'types/WarrantEntity'

/**
 * False if v is null, undefined, or an empty string, true otherwise.
 * @param {*} v value to check
 */
function hasValue(v?: unknown) {
    return v !== null && v !== undefined && v !== ''
}

function checkMandatoryFields<T extends Record<string, unknown>, F extends keyof T | Array<keyof T>>(
    issue: T,
    fields: F,
    missingFields: Array<string>,
) {
    // if fields is not a list, make it a list
    const f = Array.isArray(fields) ? fields : [fields]

    f.forEach((field) => {
        if (!hasValue(issue[field])) {
            missingFields.push(field)
        }
    })
}

function checkAtLeastOneField<T extends Record<string, unknown>, F extends Array<keyof T>>(
    issue: T,
    fields: F,
    missingFields: F,
) {
    const booleanFields = ['unlisted', 'isVwap', 'isVwapDays', 'priceMinQuota', 'noPriceMax']
    const found = fields.some((field) => {
        if (booleanFields.includes(field as string)) {
            return !!issue[field]
        } else {
            return hasValue(issue[field])
        }
    })
    if (!found) {
        missingFields.push(...fields)
    }
}

type WarrantForStatusCheck = Pick<
    EnrichedWarrantEntity,
    'cancelled' | 'instrumentId' | 'name' | 'origins' | 'subscriptionPeriods'
>

type WarrantStatus = {
    stage: 1 | 2 | 3 | 5 | 9
    status: string
    hideUntil?: string
    missingFields: Array<string>
}

function calculateWarrantStatus(warrant: WarrantForStatusCheck): WarrantStatus | null {
    if (!warrant) return null
    if (warrant.cancelled) {
        return { stage: 5, status: 'complete', missingFields: [] }
    }
    let statusStage: 1 | 2 | 3 | 5 | 9 = 1 // created
    if (warrant.subscriptionPeriods?.length > 0) {
        const today = new Date()
        const completedPeriods = warrant.subscriptionPeriods.filter(
            (p) => p.subEndDate && datefns.isBefore(new Date(p.subEndDate), today),
        )
        const ongoingPeriods = warrant.subscriptionPeriods.filter(
            (p) =>
                p.subStartDate &&
                p.subEndDate &&
                datefns.isBefore(new Date(p.subStartDate), today) &&
                datefns.isAfter(new Date(p.subEndDate), today),
        )
        const futurePeriods = warrant.subscriptionPeriods.filter(
            (p) => p.subStartDate && datefns.isAfter(new Date(p.subStartDate), today),
        )

        if (ongoingPeriods.length > 0) {
            statusStage = 2
        } else if (completedPeriods.length > 0 && futurePeriods.length === 0) {
            statusStage = 3
        }
    }
    let missingFields: Array<string> = []
    if (statusStage >= 1) {
        if (warrant.instrumentId === null) {
            // Name is only mandatory if there is not an instrument connected to the warrant
            checkMandatoryFields(warrant, 'name' as const, missingFields)
        }
        if (warrant.origins?.length) {
            warrant.origins.forEach((origin, index) => {
                const originMissingFields: Array<keyof typeof origin> = []
                if (origin.type === 'Rights issue') {
                    checkMandatoryFields(origin, 'warrantsPerUnit', originMissingFields)
                }
                if (origin.statusStage >= 4) {
                    checkMandatoryFields(origin, 'numberOfWarrants', originMissingFields)
                }
                if (originMissingFields.length > 0) {
                    missingFields.push(`origins[${index + 1}]: [${originMissingFields.join(', ')}]`)
                }
            })
        } else {
            missingFields.push('origins')
        }
        if (!warrant.subscriptionPeriods?.length) {
            missingFields.push('subscriptionPeriods')
        } else {
            warrant.subscriptionPeriods.forEach((period, index) => {
                const periodMissingFields: Array<keyof typeof period> = []
                checkMandatoryFields(
                    period,
                    [`subStartDate`, 'subEndDate', 'warrantsRequired', 'sharesReceived'],
                    periodMissingFields,
                )
                if (period.isVwap) {
                    checkMandatoryFields(period, ['vwapDiscount', 'vwapDays', 'vwapPeriodEnd'], periodMissingFields)
                    checkAtLeastOneField(period, ['priceMinQuota', 'priceMin'], periodMissingFields)
                    checkAtLeastOneField(period, ['noPriceMax', 'priceMax'], periodMissingFields)
                } else {
                    checkMandatoryFields(period, 'warrantSubPrice', periodMissingFields)
                }

                if (periodMissingFields.length > 0) {
                    missingFields.push(`subscriptionPeriods[${index + 1}]: [${periodMissingFields.join(', ')}]`)
                }
            })
        }
    }
    if (statusStage >= 2) {
        if (warrant.subscriptionPeriods?.length > 0) {
            warrant.subscriptionPeriods.forEach((period, index) => {
                const periodMissingFields: Array<keyof typeof period> = []
                checkMandatoryFields(period, 'warrantSubPrice', periodMissingFields)
                if (periodMissingFields.length > 0) {
                    missingFields.push(`subscriptionPeriods[${index + 1}]: [${periodMissingFields.join(', ')}]`)
                }
            })
        }
    }
    if (statusStage >= 3) {
        if (warrant.subscriptionPeriods?.length > 0) {
            warrant.subscriptionPeriods.forEach((period, index) => {
                const periodMissingFields: Array<keyof typeof period> = []
                checkMandatoryFields(period, 'sharesSubscribed', periodMissingFields)
                if (periodMissingFields.length > 0) {
                    missingFields.push(`subscriptionPeriods[${index + 1}]: [${periodMissingFields.join(', ')}]`)
                }
            })
        }
    }
    // removed duplicate fields
    if (missingFields.length > 0) {
        missingFields = [...new Set(missingFields)]
        return { stage: statusStage, status: 'incomplete', missingFields }
    }
    if (statusStage === 3) {
        statusStage = 9 // this is the "all done" status
    }
    return { stage: statusStage, status: 'complete', missingFields: [] }
}

function WarrantStatusDescription({
    warrantStatus,
    warrant,
    shortForm = false,
}: {
    shortForm?: boolean
    warrant: (WarrantForStatusCheck & Pick<WarrantEntity, 'statusHideUntil'>) | null
    warrantStatus?: WarrantStatus | null
}) {
    const { dateFnsLocale } = useAppLocale()
    warrantStatus = warrantStatus || calculateWarrantStatus(warrant)
    if (!warrantStatus) {
        return null
    }
    const { stage, status, missingFields } = warrantStatus
    const missingFieldsString = Array.isArray(missingFields) ? missingFields.join(', ') : missingFields

    const warrantStatusLabels = {
        1: 'Utgiven',
        2: 'Pågående',
        3: 'Avslutad',
        5: 'Avbruten',
        9: 'Klar',
    }
    const stageLabel = warrantStatusLabels[stage] || `Annat: ${stage}`
    const statusLabel = status === 'complete' ? 'klar' : 'inte klar'
    const sx = { color: status === 'complete' ? 'green' : 'red' }
    if (!warrantStatus.stage) {
        return null
    }
    if (warrantStatus.stage === 9 && warrantStatus.status === 'complete') {
        // this is the "all done" status, no need to show anything
        return (
            <Typography
                variant='body1'
                gutterBottom
                sx={[
                    {
                        display: 'block',
                    },
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
            >
                Klar
            </Typography>
        )
    }
    if (warrantStatus.stage === 5) {
        // this is the "all done" status, no need to show anything
        return (
            <Typography
                variant='body1'
                gutterBottom
                sx={[
                    {
                        display: 'block',
                    },
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
            >
                Avbruten
            </Typography>
        )
    }
    const hideUntil = warrant?.statusHideUntil || warrantStatus.hideUntil
    if (hideUntil && new Date(hideUntil) > new Date()) {
        return (
            <Typography
                variant='body1'
                gutterBottom
                sx={{
                    display: 'block',
                    color: 'orange',
                }}
            >
                Avvakta till {datefns.format(new Date(hideUntil), 'MMM d', { locale: dateFnsLocale })}
            </Typography>
        )
    }

    if (shortForm) {
        return (
            <Typography
                variant='body1'
                gutterBottom
                sx={[
                    {
                        display: 'block',
                    },
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
            >
                {stageLabel}, {statusLabel}.
            </Typography>
        )
    }
    return (
        <Typography
            variant='body1'
            gutterBottom
            sx={[
                {
                    display: 'block',
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {stageLabel}, {statusLabel}.
            {missingFieldsString && (
                <React.Fragment> Saknade fält: {missingFieldsString.split(',')?.join(', ')}</React.Fragment>
            )}
        </Typography>
    )
}

export { WarrantStatusDescription, calculateWarrantStatus }
